import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ChatbotPage from './pages/ChatbotPage';
import AboutPage from './pages/AboutPage';
import ProfilePage from './pages/ProfilePage';
import { getAccessToken, setAccessToken, getRefreshToken, setRefreshToken, getUserInfo, setUserInfo, clearAll, setSavedChatsAreaTop, setSavedChatsNeedsInit, getSystemPrompt, setSystemPrompt, removeSystemPrompt, updateProfileDataFromFacts, setSessionId, resetChatMessageCount, getChatMessageCount, getSessionId, setCurrentSubject, setSubjectPromptText, setEnableSubjectTracking, setLockCurrentSubject } from './services/sessionStorageService';
import { logout as apiLogout, getSystemPrompt as apiGetSystemPrompt, getProfileFacts, postSessionStat } from './services/apiService';
import { build_dynamic_system_prompt } from './utils/build_dynamic_system_prompt';
import { setupCleanupHandlers, resetCleanupState } from './services/cleanupService';
import logger from './utils/logger';
import './App.css';

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [accessToken, setAccessTokenState] = useState(null);
  const [refreshToken, setRefreshTokenState] = useState(null);
  const [, setUserInfoState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedSavedChat, setLoadedSavedChat] = useState(null);
  
  const navigate = useNavigate();

  // Check for existing session on app load
  useEffect(() => {
    const storedAccessToken = getAccessToken();
    const storedRefreshToken = getRefreshToken();
    const storedUserInfo = getUserInfo();
    
    if (storedAccessToken && storedRefreshToken && storedUserInfo) {
      setAccessTokenState(storedAccessToken);
      setRefreshTokenState(storedRefreshToken);
      setUserInfoState(storedUserInfo);
      setIsLoggedIn(true);
      
      // Setup browser closure detection for existing session
      setupCleanupHandlers();
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async (loginData) => {
    // Clear any existing data as a defensive measure
    clearAll();
    
    // Store new authentication data
    setAccessToken(loginData.accessToken);
    setRefreshToken(loginData.refreshToken);
    setUserInfo(loginData.user);
    
    // Initialize current subject to 'none' on login
    setCurrentSubject('none');
    
    // Initialize subject prompt text to empty string on login
    setSubjectPromptText('');
    
    // Initialize enable subject tracking to true on login
    // This ensures new sessions start with tracking enabled (default behavior)
    // The variable follows the same lifecycle as other sessionStorage variables:
    // - Created/Initialized: On login (here)
    // - Destroyed on logout: Via clearAll() in handleLogout
    // - Destroyed on browser termination: Via clearAll() in cleanupService during implicit logout
    setEnableSubjectTracking(true);
    
    // Initialize lock current subject to false on login (unchecked by default)
    setLockCurrentSubject(false);
    
    // Defensive check: Clear any existing system prompt before loading new one
    const existingSystemPrompt = getSystemPrompt();
    if (existingSystemPrompt) {
      logger.info('Clearing existing system prompt before login');
      removeSystemPrompt();
    }
    
    // Load system prompt for this session
    try {
      logger.info('Loading system prompt for new session');
      const systemPromptResponse = await apiGetSystemPrompt(loginData.accessToken);
      
      if (systemPromptResponse.success && systemPromptResponse.data) {
        logger.info('System prompt loaded successfully on login');
        setSystemPrompt(systemPromptResponse.data);
      } else {
        logger.error('Failed to load system prompt on login', { message: systemPromptResponse.message });
        // Non-fatal error - user can still use the app
      }
    } catch (error) {
      logger.error('Error loading system prompt on login', { error: error.message });
      // Non-fatal error - user can still use the app
    }
    
    // Load profile data for this session
    try {
      logger.info('Loading profile data for new session');
      const profileResponse = await getProfileFacts(loginData.accessToken);
      
      if (profileResponse.facts && Array.isArray(profileResponse.facts)) {
        logger.info('Profile data loaded successfully on login', { factCount: profileResponse.facts.length });
        updateProfileDataFromFacts(profileResponse.facts);
      } else {
        logger.error('Failed to load profile data on login', { 
          message: profileResponse.message || 'No facts array in response',
          response: profileResponse 
        });
        // Non-fatal error - user can still use the app
      }
    } catch (error) {
      logger.error('Error loading profile data on login', { error: error.message });
      // Non-fatal error - user can still use the app
    }
    
    // Build dynamic system prompt after both system prompt and profile data are loaded
    try {
      logger.info('Building dynamic system prompt after login');
      build_dynamic_system_prompt();
    } catch (error) {
      logger.error('Error building dynamic system prompt on login', { error: error.message });
      // Non-fatal error - user can still use the app
    }
    
    // RSCAF: initialize provisional defaults for this session
    try {
      setSavedChatsAreaTop(546); // provisional estimated defaultTop (px) based on measured value
      setSavedChatsNeedsInit(true);
      
    } catch (e) {
      // non-fatal
    }
    
    // Initialize per-tab session stats
    try {
      const userId = loginData?.user?.user_id;
      if (userId) {
        const sessionId = `${userId}_${Date.now()}`;
        setSessionId(sessionId);
        resetChatMessageCount();
        // Best-effort: emit login event (non-blocking)
        postSessionStat({ session_id: sessionId, event: 'login', count: 0 }, loginData.accessToken).catch(() => {});
      } else {
        logger.warn('User ID missing on login; session stats disabled for this tab');
      }
    } catch (e) {
      // Non-fatal; remain silent to user
    }

    // Update React state
    setAccessTokenState(loginData.accessToken);
    setRefreshTokenState(loginData.refreshToken);
    setUserInfoState(loginData.user);
    setIsLoggedIn(true);
    
    // Setup browser closure detection for implicit logout
    setupCleanupHandlers();
    
    logger.info('User logged in', { username: loginData.user.username });
  };

  const handleLogout = async () => {
    // Session stats: flush chat_messages (if any) and emit logout, best-effort and non-blocking
    try {
      const sessionId = getSessionId();
      if (sessionId && accessToken) {
        const count = getChatMessageCount();
        if (count > 0) {
          try { await postSessionStat({ session_id: sessionId, event: 'chat_messages', count }, accessToken); } catch {}
        }
        try { await postSessionStat({ session_id: sessionId, event: 'logout', count: 0 }, accessToken); } catch {}
      }
    } catch (e) {
      // ignore
    }

    // Call backend logout if we have a refresh token
    if (refreshToken) {
      try {
        await apiLogout(refreshToken);
        logger.info('Backend logout successful');
      } catch (error) {
        logger.error('Backend logout failed', { error: error.message });
        // Continue with frontend logout even if backend fails
      }
    }
    
    // Clear all session data
    clearAll();
    
    // Reset cleanup state for next login
    resetCleanupState();
    
    // Update React state
    setAccessTokenState(null);
    setRefreshTokenState(null);
    setUserInfoState(null);
    setIsLoggedIn(false);
    setLoadedSavedChat(null);
    
    logger.info('User logged out');
    navigate('/'); // Navigate to home page after logout
  };

  const handleLoadSavedChat = (savedChatData) => {
    logger.info('Loading saved chat in App', { chatName: savedChatData.chat_name });
    setLoadedSavedChat(savedChatData);
  };

  // Don't render routes until we've checked sessionStorage
  if (isLoading) {
    return (
      <div className="App">
        <div className="main-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Sidebar 
        isLoggedIn={isLoggedIn} 
        onLogout={handleLogout}
        onLoadSavedChat={handleLoadSavedChat}
      />
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              isLoggedIn ? (
                <HomePage />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/login" 
            element={
              isLoggedIn ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/chatbot" 
            element={
              isLoggedIn ? (
                <ChatbotPage 
                  accessToken={accessToken}
                  loadedSavedChat={loadedSavedChat}
                  onSavedChatLoaded={() => setLoadedSavedChat(null)}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/about" 
            element={
              isLoggedIn ? (
                <AboutPage />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/profile" 
            element={
              isLoggedIn ? (
                <ProfilePage />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

export default App; 